import { Request, Response as ExpressResponse } from 'express';
import { GraphQLError } from 'graphql';
import { StatusCodes } from 'http-status-codes';
import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError, ErrorLink, ErrorResponse } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { always, tryCatch } from 'ramda';
import fetch from 'isomorphic-fetch';
import { History } from 'history';
import { RouteContext } from '@moda/portal-platform';
import { SSRContext } from '../../types/SSRContext';
import { parseUser } from '../../lib/user';
import { decode as decodePreferences } from '../../hooks/usePreferences/tools';
import { parseCookies, getCookies } from '../../hooks/useCookies';
import { getUserFromCookies } from '../../hooks/useUser';
import { hrefFor } from '../../routers';
import { CONFIG } from '../../config';
import { isDevelopment } from '../../lib/isEnv';
import { ONE_YEAR_SECONDS, COUNTRIES, DEFAULT_COUNTRY } from '../../constants';
import { createCache, initializeCache } from './cache';
import { localTypeDefs } from './localTypeDefs';
import { getSearchApiHeaders } from './getSearchApiHeaders';

type NetworkError = {
  response: Response;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  statusCode: number;
};

type GraphQLErrorWithCode = GraphQLError & { code: string };

const isUnauthorizedError = ({ networkError, graphQLErrors }: Partial<ErrorResponse>) =>
  (Array.isArray(graphQLErrors) &&
    graphQLErrors.some(error => (error as GraphQLErrorWithCode).code === 'unauthorized')) ||
  (networkError as NetworkError)?.statusCode === StatusCodes.UNAUTHORIZED;

const createServerErrorHandler = ({
  routeContext,
  ssrContext
}: {
  routeContext: RouteContext;
  ssrContext: SSRContext;
}): ErrorLink.ErrorHandler => ({ graphQLErrors, networkError, operation, response }) => {
  if (isUnauthorizedError({ graphQLErrors, networkError })) {
    routeContext.url = hrefFor.LogoutPage();
    ssrContext.ignoreErrors = true;
  }

  // eslint-disable-next-line no-console
  console.error(
    JSON.stringify({
      msg: `Apollo: GraphQL Error during operation "${operation.operationName}"`,
      level: 50,
      graphQLErrors,
      networkError,
      operation,
      response
    })
  );
};

const createClientErrorHandler = (history: History): ErrorLink.ErrorHandler => ({
  graphQLErrors,
  networkError,
  operation,
  response
}) => {
  if (isUnauthorizedError({ graphQLErrors, networkError })) {
    if (response) response.errors = undefined;
    history.replace(hrefFor.LogoutPage());
  }

  // eslint-disable-next-line no-console
  console.error(`Apollo: GraphQL Error during operation "${operation.operationName}"\n%o`, {
    graphQLErrors,
    networkError,
    operation,
    response
  });
};

const commonConfiguration = {
  link: ApolloLink.from([
    new RestLink({
      customFetch: fetch,
      endpoints: {
        cart: CONFIG.MODA_API_URL,
        preferences: CONFIG.PREFERENCES_API_URL,
        user: CONFIG.USER_API_URL,
        self: CONFIG.DISCOVERY_API_URL // TODO: try to figure out if there's a way to use a relative path
      },
      uri: CONFIG.MODA_API_URL,
      headers: {
        'Content-Type': 'application/vnd.api+json'
      },
      fieldNameNormalizer: name => name.replace(/[^a-zA-Z0-9_]/g, '')
    }),
    createHttpLink({ uri: CONFIG.SEARCH_API_GRAPHQL_ENDPOINT, fetch })
  ]),
  typeDefs: localTypeDefs,
  resolvers: {}
};

export const createClientSideApolloClient = (history: History) => {
  const cache = createCache();
  const headersLink = setContext((_, { headers }) => {
    const cookies = getCookies();
    const searchApiHeaders = getSearchApiHeaders(cookies);
    const { accessToken } = getUserFromCookies();

    return {
      headers: {
        ...headers,
        ...searchApiHeaders,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  initializeCache(cache, {});

  return new ApolloClient({
    ...commonConfiguration,
    link: onError(createClientErrorHandler(history))
      .concat(headersLink)
      .concat(commonConfiguration.link),
    cache: cache.restore(window.__APOLLO_STATE__),
    connectToDevTools: isDevelopment()
      ? true
      : tryCatch(devTools => JSON.parse(devTools), always(false))(window.__ENV__?.DEV_TOOLS)
  });
};

export const createServerSideApolloClient = ({
  req,
  routeContext,
  ssrContext,
  res
}: {
  req: Request;
  routeContext: RouteContext;
  ssrContext: SSRContext;
  res: ExpressResponse;
}) => {
  const cache = createCache();

  const preferences = decodePreferences(req.cookies.preferences);
  const country =
    COUNTRIES.find(country => country.id === preferences.countryId) || DEFAULT_COUNTRY;
  const clientCountryCode = req.headers['cloudfront-viewer-country'] || country.alpha2Code;

  const cookies = parseCookies({
    ...req.cookies,
    clientCountryCode
  });

  res.cookie('clientCountryCode', clientCountryCode, {
    path: '/',
    maxAge: ONE_YEAR_SECONDS
  });

  const user = parseUser(cookies.fullUser);

  const headersLink = setContext((_, { headers }) => {
    const searchApiHeaders = getSearchApiHeaders(cookies);
    const { accessToken } = user;

    return {
      headers: {
        ...headers,
        searchApiHeaders,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  initializeCache(cache, { user, cookies });

  return new ApolloClient({
    ...commonConfiguration,
    link: onError(createServerErrorHandler({ routeContext, ssrContext }))
      .concat(headersLink)
      .concat(commonConfiguration.link),
    ssrMode: true,
    cache: cache
  });
};
